import { isValidBIC, isValidIBAN } from 'ibantools';
import type { UpdateCompanyBankDetailsRequest } from '@/types/api/companies';

/**
 * Field length caps mirror the database schema (CompanyBankDetails table).
 */
export const BANK_DETAIL_MAX_LENGTHS = {
  currency: 10,
  sortCode: 10,
  accountNumber: 50,
  accountName: 200,
  ibanNumber: 50,
  swiftBicCode: 20,
} as const satisfies Record<keyof UpdateCompanyBankDetailsRequest, number>;

/**
 * Map of field name -> inline error message.
 * An empty object (or all-undefined values) indicates a valid row.
 */
export type BankDetailFieldErrors = Partial<Record<keyof UpdateCompanyBankDetailsRequest, string>>;

const UK_SORT_CODE_REGEX = /^\d{2}-?\d{2}-?\d{2}$/;

function normalise(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/**
 * Validates a non-empty value against a max length and an optional format
 * predicate. Returns an error message string, or undefined if valid.
 */
function validateField(
  value: string,
  maxLength: number,
  lengthMsg: string,
  formatPredicate?: (v: string) => boolean,
  formatMsg?: string,
): string | undefined {
  if (value.length === 0) return undefined;
  if (value.length > maxLength) return lengthMsg;
  if (formatPredicate && !formatPredicate(value)) return formatMsg;
  return undefined;
}

/**
 * Validates a single bank-details row against the rules documented
 * in the brief. All fields are optional — empty values never produce
 * an error. Returns a map of field -> human-readable error message.
 */
export function validateBankDetailRow(
  row: UpdateCompanyBankDetailsRequest,
  validCurrencyCodes: ReadonlySet<string>,
): BankDetailFieldErrors {
  const errors: BankDetailFieldErrors = {};

  const currency = normalise(row.currency);
  const currencyError = validateField(
    currency,
    BANK_DETAIL_MAX_LENGTHS.currency,
    `Must be ${BANK_DETAIL_MAX_LENGTHS.currency} characters or fewer`,
    (v) => validCurrencyCodes.has(v),
    'Select a currency from the list',
  );
  if (currencyError) errors.currency = currencyError;

  const sortCode = normalise(row.sortCode);
  const sortCodeError = validateField(
    sortCode,
    BANK_DETAIL_MAX_LENGTHS.sortCode,
    `Must be ${BANK_DETAIL_MAX_LENGTHS.sortCode} characters or fewer`,
    (v) => UK_SORT_CODE_REGEX.test(v),
    'Use format 12-34-56 or 123456',
  );
  if (sortCodeError) errors.sortCode = sortCodeError;

  const accountNumber = normalise(row.accountNumber);
  if (accountNumber.length > BANK_DETAIL_MAX_LENGTHS.accountNumber) {
    errors.accountNumber = `Must be ${BANK_DETAIL_MAX_LENGTHS.accountNumber} characters or fewer`;
  }

  const accountName = normalise(row.accountName);
  if (accountName.length > BANK_DETAIL_MAX_LENGTHS.accountName) {
    errors.accountName = `Must be ${BANK_DETAIL_MAX_LENGTHS.accountName} characters or fewer`;
  }

  const iban = stripIbanSpaces(normalise(row.ibanNumber));
  const ibanError = validateField(
    iban,
    BANK_DETAIL_MAX_LENGTHS.ibanNumber,
    `Must be ${BANK_DETAIL_MAX_LENGTHS.ibanNumber} characters or fewer`,
    isValidIBAN,
    'Enter a valid IBAN',
  );
  if (ibanError) errors.ibanNumber = ibanError;

  const bic = normalise(row.swiftBicCode).toUpperCase();
  const bicError = validateField(
    bic,
    BANK_DETAIL_MAX_LENGTHS.swiftBicCode,
    `Must be ${BANK_DETAIL_MAX_LENGTHS.swiftBicCode} characters or fewer`,
    isValidBIC,
    'Enter a valid SWIFT/BIC code',
  );
  if (bicError) errors.swiftBicCode = bicError;

  return errors;
}

/**
 * True if at least one field on the row has an error.
 */
export function hasBankDetailErrors(errors: BankDetailFieldErrors): boolean {
  return Object.values(errors).some((v) => v !== undefined && v !== '');
}

/**
 * Strips all whitespace from an IBAN string. Use before sending to the API
 * or running format validation.
 */
export function stripIbanSpaces(value: string): string {
  return value.replace(/\s+/g, '');
}

/**
 * Formats an IBAN for display by inserting a single space every 4 characters.
 * Non-alphanumeric characters (including existing whitespace) are stripped
 * first, and the remainder is upper-cased to match IBAN conventions.
 */
export function formatIbanDisplay(value: string): string {
  const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return cleaned.replace(/(.{4})/g, '$1 ').trimEnd();
}
